import { Moment, MomentInput } from "moment";

import { Mode } from "./index";

type GetLimitedDays = {
  daysDiff: number;
  maxDaysToLeft: number;
  maxDaysToRight: number;
};

const getLimitedDays = ({ daysDiff, maxDaysToLeft, maxDaysToRight }: GetLimitedDays): number => {
  // daysDiff below 0 means expanding bar to the previous dates
  if (daysDiff < maxDaysToLeft) {
    return maxDaysToLeft;
  } else if (daysDiff > maxDaysToRight) {
    return maxDaysToRight;
  }
  return daysDiff;
};

type LimitDaysDiffIfNeeded = {
  daysDiff: number;
  minDate: Moment;
  defaultStartDate: Moment;
  defaultEndDate: Moment;
  calendarEndDate: Moment;
};

const limitDaysDiffIfNeededForStart = ({
  daysDiff,
  minDate,
  defaultStartDate,
  defaultEndDate
}: Omit<LimitDaysDiffIfNeeded, "calendarEndDate">): number => {
  const daysDiffToMinDate = minDate.diff(defaultStartDate, "days") - 1;
  const maxDaysToLeft = defaultStartDate.isBefore(minDate) ? 0 : daysDiffToMinDate;
  const maxDaysToRight = defaultEndDate.diff(defaultStartDate, "days");
  return getLimitedDays({ daysDiff, maxDaysToLeft, maxDaysToRight });
};

const limitDaysDiffIfNeededForEnd = ({
  daysDiff,
  calendarEndDate,
  defaultStartDate,
  defaultEndDate
}: Omit<LimitDaysDiffIfNeeded, "minDate">): number => {
  const maxDaysToLeft = defaultStartDate.diff(defaultEndDate, "days");
  const maxDaysToRight = calendarEndDate.diff(defaultEndDate, "days");
  return getLimitedDays({ daysDiff, maxDaysToLeft, maxDaysToRight });
};

const limitDaysDiffIfNeededForStartEnd = ({
  daysDiff,
  minDate,
  calendarEndDate,
  defaultStartDate,
  defaultEndDate
}: LimitDaysDiffIfNeeded): number => {
  const daysDiffToMinDate = minDate.diff(defaultStartDate, "days") - 1;
  const maxDaysToLeft = defaultStartDate.isBefore(minDate) ? 0 : daysDiffToMinDate;
  const maxDaysToRight = calendarEndDate.diff(defaultEndDate, "days");
  return getLimitedDays({ daysDiff, maxDaysToLeft, maxDaysToRight });
};

type GetUpdatedDatesReturn = {
  startDate: string;
  endDate: string;
  daysDiffLimited: number;
};

const getUpdatedDates = (
  mode: Mode,
  daysDiff: number,
  defaultStartDate: string,
  defaultEndDate: string,
  calendarEndDate: string,
  utc: (date?: MomentInput) => Moment,
  formatDatabaseDateTime: (date?: MomentInput) => string,
  pastDaysLimit: number
): GetUpdatedDatesReturn => {
  const minDate = utc().subtract(pastDaysLimit, "days");
  const defaultStartDateUtc = utc(defaultStartDate);
  const defaultEndDateUtc = utc(defaultEndDate);
  const calendarEndDateUtc = utc(calendarEndDate);

  let startDate = utc(defaultStartDate);
  let endDate = utc(defaultEndDate);
  let daysDiffLimited = daysDiff;

  if (mode === "start_end") {
    daysDiffLimited = limitDaysDiffIfNeededForStartEnd({
      daysDiff,
      minDate,
      calendarEndDate: calendarEndDateUtc,
      defaultStartDate: defaultStartDateUtc,
      defaultEndDate: defaultEndDateUtc
    });
    startDate = startDate.add(daysDiffLimited, "days");
    endDate = endDate.add(daysDiffLimited, "days");
  } else if (mode === "start") {
    daysDiffLimited = limitDaysDiffIfNeededForStart({
      daysDiff,
      minDate,
      defaultStartDate: defaultStartDateUtc,
      defaultEndDate: defaultEndDateUtc
    });
    startDate = startDate.add(daysDiffLimited, "days");
  } else if (mode === "end") {
    daysDiffLimited = limitDaysDiffIfNeededForEnd({
      daysDiff,
      calendarEndDate: calendarEndDateUtc,
      defaultStartDate: defaultStartDateUtc,
      defaultEndDate: defaultEndDateUtc
    });
    endDate = endDate.add(daysDiffLimited, "days");
  }
  return {
    startDate: formatDatabaseDateTime(utc(startDate)),
    endDate: formatDatabaseDateTime(utc(endDate)),
    daysDiffLimited
  };
};

export default getUpdatedDates;
